import { useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Switch } from '@/components/ui/switch';
import { Slider } from '@/components/ui/slider';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Calculator, RotateCcw, Activity, User, Ruler, Scale, AlertCircle } from 'lucide-react';
import {
  calculateBMI,
  lbsToKg,
  ftInToCm,
  validateAge,
  validateWeight,
  validateHeight,
  validateWaist,
  calculateWaistToHeight,
} from '@/lib/bmi-utils';

export const BMICalculator = ({ onCalculate }) => {
  const [unit, setUnit] = useState('metric');
  const [athleteMode, setAthleteMode] = useState(false);
  const [gender, setGender] = useState('');
  const [age, setAge] = useState('');
  const [weight, setWeight] = useState('');
  const [heightCm, setHeightCm] = useState('');
  const [heightFt, setHeightFt] = useState('');
  const [heightIn, setHeightIn] = useState('');
  const [waist, setWaist] = useState('');
  const [errors, setErrors] = useState({});

  const validateForm = useCallback(() => {
    const newErrors = {};

    // Validate age
    const ageValidation = validateAge(age);
    if (!ageValidation.valid) {
      newErrors.age = ageValidation.message;
    }

    // Validate weight
    let weightKg = Number(weight);
    if (unit === 'us') {
      weightKg = lbsToKg(weightKg);
    }
    const weightValidation = validateWeight(weightKg);
    if (!weightValidation.valid) {
      newErrors.weight = weightValidation.message;
    }

    // Validate height
    let heightCmVal;
    if (unit === 'metric') {
      heightCmVal = Number(heightCm);
    } else {
      heightCmVal = ftInToCm(Number(heightFt) || 0, Number(heightIn) || 0);
    }
    const heightValidation = validateHeight(heightCmVal);
    if (!heightValidation.valid) {
      newErrors.height = heightValidation.message;
    }

    // Validate waist (optional)
    if (waist) {
      const waistValidation = validateWaist(waist);
      if (!waistValidation.valid) {
        newErrors.waist = waistValidation.message;
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [unit, age, weight, heightCm, heightFt, heightIn, waist]);

  const handleCalculate = useCallback(() => {
    if (!validateForm()) return;

    // Convert values to metric
    let weightKg = Number(weight);
    if (unit === 'us') {
      weightKg = lbsToKg(weightKg);
    }

    let heightCmVal;
    if (unit === 'metric') {
      heightCmVal = Number(heightCm);
    } else {
      heightCmVal = ftInToCm(Number(heightFt) || 0, Number(heightIn) || 0);
    }

    // Calculate BMI
    const bmi = calculateBMI(weightKg, heightCmVal);

    // Calculate waist-to-height ratio if waist is provided
    let waistToHeightRatio = null;
    if (waist) {
      waistToHeightRatio = calculateWaistToHeight(Number(waist), heightCmVal);
    }

    // Call parent callback with results
    onCalculate({
      bmi,
      weight: weightKg,
      height: heightCmVal,
      age: Number(age),
      gender,
      unit,
      athleteMode,
      waist: waist ? Number(waist) : null,
      waistToHeightRatio,
    });
  }, [validateForm, unit, weight, heightCm, heightFt, heightIn, waist, age, gender, athleteMode, onCalculate]);

  const handleReset = () => {
    setAge('');
    setWeight('');
    setHeightCm('');
    setHeightFt('');
    setHeightIn('');
    setWaist('');
    setGender('');
    setErrors({});
  };

  return (
    <Card className="card-elevated overflow-hidden">
      <CardHeader className="bg-primary/5 border-b border-border pb-6">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-primary/10 flex items-center justify-center">
              <Calculator className="w-5 h-5 sm:w-6 sm:h-6 text-primary" />
            </div>
            <div>
              <CardTitle className="text-lg sm:text-xl">BMI Calculator</CardTitle>
              <CardDescription className="text-xs sm:text-sm">Enter your details to calculate</CardDescription>
            </div>
          </div>
          
          {/* Athlete Mode Toggle */}
          <div className="flex items-center gap-2 sm:gap-3 bg-muted/50 rounded-lg px-3 py-2">
            <Activity className={`w-4 h-4 sm:w-5 sm:h-5 transition-colors ${athleteMode ? 'text-primary' : 'text-muted-foreground'}`} />
            <span className="text-sm font-medium text-foreground">Athlete</span>
            <Switch
              checked={athleteMode}
              onCheckedChange={setAthleteMode}
              className="data-[state=checked]:bg-primary"
            />
          </div>
        </div>
      </CardHeader>
      
      <CardContent className="p-6">
        <div className="space-y-6">
          {/* Unit Selector */}
          <div>
            <Label className="text-sm font-medium mb-2 block">Unit System</Label>
            <Tabs value={unit} onValueChange={setUnit} className="w-full">
              <TabsList className="grid w-full grid-cols-2 h-11">
                <TabsTrigger value="metric" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
                  Metric (kg/cm)
                </TabsTrigger>
                <TabsTrigger value="us" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
                  US (lbs/ft+in)
                </TabsTrigger>
              </TabsList>
            </Tabs>
          </div>

          {/* Age & Gender Row */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* Age */}
            <div className="space-y-2">
              <Label htmlFor="age" className="text-sm font-medium flex items-center gap-2">
                <User className="w-4 h-4 text-muted-foreground" />
                Age
              </Label>
              <Input
                id="age"
                type="number"
                placeholder="Enter age (2-120)"
                value={age}
                onChange={(e) => setAge(e.target.value)}
                className={`h-11 ${errors.age ? 'border-destructive focus:ring-destructive' : ''}`}
                min="2"
                max="120"
              />
              {errors.age && (
                <p className="text-xs text-destructive flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" />
                  {errors.age}
                </p>
              )}
            </div>

            {/* Gender */}
            <div className="space-y-2">
              <Label className="text-sm font-medium flex items-center gap-2">
                <User className="w-4 h-4 text-muted-foreground" />
                Gender (Optional)
              </Label>
              <Select value={gender} onValueChange={setGender}>
                <SelectTrigger className="h-11">
                  <SelectValue placeholder="Select gender" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="male">Male</SelectItem>
                  <SelectItem value="female">Female</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Height Input */}
          <div className="space-y-2">
            <Label className="text-sm font-medium flex items-center gap-2">
              <Ruler className="w-4 h-4 text-muted-foreground" />
              Height
            </Label>
            {unit === 'metric' ? (
              <Input
                type="number"
                placeholder="Height in cm (e.g., 175)"
                value={heightCm}
                onChange={(e) => setHeightCm(e.target.value)}
                className={`h-11 ${errors.height ? 'border-destructive focus:ring-destructive' : ''}`}
                min="50"
                max="280"
              />
            ) : (
              <div className="grid grid-cols-2 gap-3">
                <Input
                  type="number"
                  placeholder="Feet"
                  value={heightFt}
                  onChange={(e) => setHeightFt(e.target.value)}
                  className={`h-11 ${errors.height ? 'border-destructive focus:ring-destructive' : ''}`}
                  min="1"
                  max="8"
                />
                <Input
                  type="number"
                  placeholder="Inches"
                  value={heightIn}
                  onChange={(e) => setHeightIn(e.target.value)}
                  className={`h-11 ${errors.height ? 'border-destructive focus:ring-destructive' : ''}`}
                  min="0"
                  max="11"
                />
              </div>
            )}
            {errors.height && (
              <p className="text-xs text-destructive flex items-center gap-1">
                <AlertCircle className="w-3 h-3" />
                {errors.height}
              </p>
            )}
          </div>

          {/* Weight Input */}
          <div className="space-y-2">
            <Label className="text-sm font-medium flex items-center gap-2">
              <Scale className="w-4 h-4 text-muted-foreground" />
              Weight
            </Label>
            <Input
              type="number"
              placeholder={unit === 'metric' ? 'Weight in kg (e.g., 70)' : 'Weight in lbs (e.g., 154)'}
              value={weight}
              onChange={(e) => setWeight(e.target.value)}
              className={`h-11 ${errors.weight ? 'border-destructive focus:ring-destructive' : ''}`}
              min="20"
              max={unit === 'metric' ? '300' : '660'}
            />
            {errors.weight && (
              <p className="text-xs text-destructive flex items-center gap-1">
                <AlertCircle className="w-3 h-3" />
                {errors.weight}
              </p>
            )}
          </div>

          {/* Waist Input (Optional) */}
          <div className="space-y-2">
            <Label className="text-sm font-medium flex items-center gap-2">
              <Ruler className="w-4 h-4 text-muted-foreground" />
              Waist Circumference (Optional)
            </Label>
            <Input
              type="number"
              placeholder="Waist in cm (e.g., 80)"
              value={waist}
              onChange={(e) => setWaist(e.target.value)}
              className={`h-11 ${errors.waist ? 'border-destructive focus:ring-destructive' : ''}`}
              min="30"
              max="200"
            />
            {errors.waist && (
              <p className="text-xs text-destructive flex items-center gap-1">
                <AlertCircle className="w-3 h-3" />
                {errors.waist}
              </p>
            )}
            <p className="text-xs text-muted-foreground">
              Add waist measurement for waist-to-height ratio analysis
            </p>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 pt-2">
            <Button
              onClick={handleCalculate}
              className="flex-1 h-12 btn-primary-gradient text-base"
            >
              <Calculator className="w-5 h-5 mr-2" />
              Calculate BMI
            </Button>
            <Button
              variant="outline"
              onClick={handleReset}
              className="h-12 px-4"
            >
              <RotateCcw className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default BMICalculator;
